<script setup lang="ts">
/**
 * LEVEL-UP BANNER — a level earned anywhere in the hold gets its own beat.
 *
 * Before this, a level was only ever named on the fight outcome panel, so one
 * earned from an errand, a raid, a quest or the tutorial passed in silence
 * (QA 86cb91hh1). Mounted once beside ToastHost; raised by `level-up.ts`, which
 * watches the account level rather than trusting each faucet to announce.
 *
 * A struck rune-plate over a brief scrim: loud enough to be a moment, gone in
 * four seconds, dismissable by clicking anywhere on it, and reduced to a plain
 * fade for anyone who asked for less motion.
 */
import { watch, onUnmounted, ref } from 'vue';
import { levelUp, clearNotice, PER_LEVEL_GROWTH } from '../game/level-up';
import { t, tc } from '../i18n';
import * as sfx from '../game/sfx';

const SHOW_MS = 4200;
let timer = 0;
const shown = ref(false);

watch(() => levelUp.notice, (n) => {
  window.clearTimeout(timer);
  shown.value = !!n;
  if (!n) return;
  // the level's own sting — the same clash the ring uses when a foe falls
  try { sfx.clash(); } catch { /* audio not unlocked yet; the banner still lands */ }
  timer = window.setTimeout(clearNotice, SHOW_MS);
});

onUnmounted(() => window.clearTimeout(timer));

/** Growth for the whole jump — a fat payout can cross two levels at once. */
const grow = (per: number) => per * (levelUp.notice?.gained ?? 1);
</script>

<template>
  <Transition name="lvb">
    <div
      v-if="shown && levelUp.notice"
      class="lvb"
      role="status"
      aria-live="polite"
      @click="clearNotice()"
    >
      <div class="lvb-plate">
        <span class="lvb-rune" aria-hidden="true">&#5869;</span>
        <p class="lvb-kick">{{ t('levelUp.kicker') }}</p>
        <p class="lvb-level">{{ t('levelUp.level', { level: levelUp.notice.level }) }}</p>
        <p v-if="levelUp.notice.gained > 1" class="lvb-multi">
          {{ tc('levelUp.levelsAtOnce', levelUp.notice.gained) }}
        </p>
        <ul class="lvb-gains">
          <li><i>{{ t('levelUp.hp') }}</i><b>+{{ grow(PER_LEVEL_GROWTH.maxHp) }}</b></li>
          <li><i>{{ t('levelUp.attack') }}</i><b>+{{ grow(PER_LEVEL_GROWTH.attack) }}</b></li>
          <li><i>{{ t('levelUp.defense') }}</i><b>+{{ grow(PER_LEVEL_GROWTH.defense) }}</b></li>
          <li><i>{{ t('levelUp.magic') }}</i><b>+{{ grow(PER_LEVEL_GROWTH.magic) }}</b></li>
        </ul>
        <p class="lvb-hint">{{ t('levelUp.dismiss') }}</p>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.lvb {
  position: fixed; inset: 0; z-index: 460; /* over the toasts, under a modal */
  display: grid; place-items: center;
  padding: 16px;
  background: radial-gradient(ellipse at center, rgba(6, 12, 22, 0.62), rgba(4, 7, 14, 0.86));
  cursor: pointer;
}
/* a struck plate: hewn corners, ice rim, the aurora caught along the top edge */
.lvb-plate {
  position: relative;
  width: min(360px, 100%);
  padding: 20px 22px 14px;
  text-align: center;
  clip-path: var(--clip-hewn);
  background: var(--surface-plank, #131a26);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
  box-shadow:
    inset 0 0 0 1px rgba(201, 165, 88, 0.5),
    inset 0 1px 0 rgba(245, 218, 150, 0.16),
    inset 0 0 46px rgba(120, 180, 230, 0.10),
    0 18px 44px rgba(0, 0, 0, 0.66);
}
.lvb-rune {
  display: block;
  font-size: 26px; line-height: 1;
  color: var(--color-gold-bright, #e9c166);
  text-shadow: 0 0 18px rgba(233, 193, 102, 0.5);
}
.lvb-kick {
  margin-top: 6px;
  font-family: var(--font-label, sans-serif);
  font-size: 9.5px; letter-spacing: 0.16em; text-transform: uppercase;
  color: #8fa2b6;
}
.lvb-level {
  margin-top: 2px;
  font-family: var(--font-heading, serif);
  font-size: clamp(26px, 7vw, 34px); line-height: 1.05;
  color: var(--color-gold-bright, #e9c166);
  text-shadow: 0 0 24px rgba(233, 193, 102, 0.34), 0 -1px 0 rgba(0, 0, 0, 0.9);
}
.lvb-multi {
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--color-gold, #c9a84c);
}
/* the growth, in the ward-card stat grammar: dim key, struck value */
.lvb-gains {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 2px 16px;
  margin: 12px 0 4px;
  padding: 10px 2px 2px;
  border-top: 1px solid rgba(201, 165, 88, 0.22);
  list-style: none;
}
.lvb-gains li { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.lvb-gains i {
  font-style: normal;
  font-family: var(--font-label, sans-serif);
  font-size: 9px; letter-spacing: 0.06em; text-transform: uppercase;
  color: #8fa2b6;
}
.lvb-gains b {
  font-weight: 400;
  font-family: var(--font-heading, serif);
  font-size: 15px;
  color: #d7e0ec;
  font-variant-numeric: tabular-nums;
}
.lvb-hint {
  margin-top: 8px;
  font-family: var(--font-label, sans-serif);
  font-size: 8.5px; letter-spacing: 0.1em; text-transform: uppercase;
  color: #6f8095;
}

.lvb-enter-active, .lvb-leave-active { transition: opacity 260ms ease; }
.lvb-enter-from, .lvb-leave-to { opacity: 0; }
.lvb-enter-active .lvb-plate {
  animation: lvb-strike 420ms cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes lvb-strike {
  from { transform: scale(0.86) translateY(10px); }
  to { transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .lvb-enter-active .lvb-plate { animation: none; }
}
</style>
